import { useState, useCallback, useRef, useEffect } from "react";
import { FiBarChart2, FiCheckCircle, FiClipboard, FiXCircle } from "react-icons/fi";
import { getRouteApi, useRouteContext } from "@tanstack/react-router";
import { fetchAuthenticated } from "@/queries/shared";
import { useMonthlyCertificationReports } from "@/queries";
import ReportFrame from "@/components/shared/ReportFrame";
import {
  buildMonthStartFilter,
  isMonthlyAttendanceReport,
} from "@/utils/reportFilters";

const rootRoute = getRouteApi("__root__");

type Report = {
  id: string;
  reportId: number;
  label: string;
  serverURL: string;
  isSupplemental: boolean;
  usesDynamicParameters: boolean;
};

type ReportWithStatus = Report & {
  isRead: boolean;
};

const buildReportUrl = (
  serverURL: string,
  reportingHistoryId?: string | null,
  reportMonthYear?: string | null,
  usesDynamicParameters = false
): string => {
  const allowedPrefix = "reports/powerbi/";
  if (!serverURL.startsWith(allowedPrefix)) {
    console.error("Unexpected report URL:", serverURL);
    return "";
  }

  const base = `/${serverURL}`;
  const filters: string[] = [];

  if (reportingHistoryId) {
    if (usesDynamicParameters) {
      filters.push(`Main/ReportingHistoryId+eq+${reportingHistoryId}`);
    } else {
      filters.push(
        `Main/FromReportingHistoryId+le+${reportingHistoryId}` +
          `+and+Main/ToReportingHistoryId+gt+${reportingHistoryId}`
      );
    }
  }

  if (reportMonthYear) {
    if (usesDynamicParameters) {
      const [year, month] = reportMonthYear.split(" ");

      const monthStart = `${year}-${month}-01`;

      filters.push(buildMonthStartFilter(monthStart));
    } else {
      filters.push(
        `MonthStarts/MonthYear+eq+%27${encodeURIComponent(reportMonthYear)}%27`
      );
    }
  }

  if (filters.length === 0) {
    return base;
  }

  return `${base}+and+${filters.join("+and+")}`;
};

function SectionHeader({
  icon,
  title,
  count,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
}) {
  return (
    <div className="reports-tab__section-header">
      <div className="d-flex align-items-center gap-2">
        {icon}
        <span className="reports-tab__section-header-title">{title}</span>
      </div>
      <span className="reports-tab__count-badge">
        {count} report{count !== 1 ? "s" : ""}
      </span>
    </div>
  );
}

function ReportRow({
  report,
  isSelected,
  canReviewReports,
  onClick,
}: {
  report: ReportWithStatus;
  isSelected: boolean;
  canReviewReports: boolean;
  onClick: () => void;
}) {
  const isVerified = canReviewReports && report.isRead;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`reports-tab__row${isSelected ? " reports-tab__row--selected" : ""}`}
      aria-current={isSelected ? "true" : undefined}
    >
      {report.isSupplemental ? null : isVerified ? (
        <FiCheckCircle
          size={20}
          className="reports-tab__icon--read"
          aria-label="Verified"
        />
      ) : (
        <FiXCircle
          size={20}
          className="reports-tab__icon--unread"
          aria-label="Not yet verified"
        />
      )}
      <span>
        <strong>{report.label.split(" ")[0]}</strong>{" "}
        {report.label.split(" ").slice(1).join(" ")}
      </span>
    </button>
  );
}

type ReportsTabProps = {
  leaid: string;
  reportingHistoryId?: string | null;
  reportMonthYear?: string | null;
  canCertify: boolean;
  canReviewReports?: boolean;
  hasLockedRevision?: boolean;
  mostRecentRevisionId?: string | null;
  readSet: Set<string>;
  persistedReadIds: Set<string>;
  onReportRead: (id: string) => void;
  onReportUnread?: (id: string) => void;
  onAutoLock?: (
    revisionId: string,
    onLockSuccess?: (revisionId: string) => void,
    onLockError?: () => void
  ) => void;
  onReportViewed?: (
    reportId: string,
    overrideLockId?: string
  ) => Promise<boolean>;
  isCycleEnded?: boolean;
};

export default function ReportsTab({
  leaid,
  reportingHistoryId,
  reportMonthYear,
  canCertify,
  canReviewReports = false,
  hasLockedRevision = false,
  mostRecentRevisionId = null,
  readSet,
  persistedReadIds,
  onReportRead,
  onReportUnread,
  onAutoLock,
  onReportViewed,
  isCycleEnded = false,
}: ReportsTabProps) {
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [cookieReady, setCookieReady] = useState(false);
  const [cookieError, setCookieError] = useState(false);
  const autoLockFiredRef = useRef(false);
  const loggedReportsRef = useRef<Set<string> | null>(null);
  const prevPersistedSizeRef = useRef(0);
  const globalState = rootRoute.useLoaderData();
  const { hasFeature } = useRouteContext({ from: "/$leaid" });

  const { data: rawReportItems = [] } = useMonthlyCertificationReports(leaid);
  const reportItems = rawReportItems.filter(
    (item) =>
      hasFeature("Attendance") || !isMonthlyAttendanceReport(item.serverURL)
  );

  const isSupplemental = (item: (typeof reportItems)[number]) =>
    item.name.toLowerCase().includes("supplemental");
  const usesDynamicParameters = (item: (typeof reportItems)[number]) =>
    item.serverURL.toLowerCase().includes("monthlycert_1_5") ||
    isMonthlyAttendanceReport(item.serverURL);
  const aggregateItems = reportItems.filter((item) => !isSupplemental(item));
  const supplementalItems = reportItems.filter(isSupplemental);

  const aggregateReports: Report[] = aggregateItems.map((item, index) => ({
    id: item.reportId.toString(),
    reportId: item.reportId,
    label: `1.${index + 1} – ${item.name}`,
    serverURL: item.serverURL,
    isSupplemental: false,
    usesDynamicParameters: usesDynamicParameters(item),
  }));

  const supplementalReports: Report[] = supplementalItems.map((item, index) => ({
    id: item.reportId.toString(),
    reportId: item.reportId,
    label: `5.${index + 1} – ${item.name}`,
    serverURL: item.serverURL,
    isSupplemental: true,
    usesDynamicParameters: usesDynamicParameters(item),
  }));

  useEffect(() => {
    let cancelled = false;
    setCookieReady(false);
    setCookieError(false);

    async function initCookie() {
      try {
        await fetchAuthenticated(
          globalState,
          `/ReportDirectory/${leaid}/getcookie`
        );
        if (!cancelled) {
          setCookieReady(true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to initialize report cookie:", error);
        setCookieError(true);
      }
    }
    initCookie();

    return () => {
      cancelled = true;
    };
  }, [leaid, globalState]);

  useEffect(() => {
    autoLockFiredRef.current = false;
    loggedReportsRef.current = new Set(persistedReadIds);
    prevPersistedSizeRef.current = persistedReadIds.size;
  }, [reportingHistoryId]);

  if (loggedReportsRef.current === null) {
    loggedReportsRef.current = new Set(persistedReadIds);
  }

  useEffect(() => {
    const logged = loggedReportsRef.current;
    if (!logged) return;

    const prevSize = prevPersistedSizeRef.current;
    const currSize = persistedReadIds.size;

    if (currSize < prevSize) {
      loggedReportsRef.current = new Set(persistedReadIds);
    } else if (currSize > prevSize) {
      persistedReadIds.forEach((id) => logged.add(id));
    }

    prevPersistedSizeRef.current = currSize;
  }, [persistedReadIds]);

  const handleSelectReport = useCallback(
    (report: Report) => {
      setSelectedReportId(report.id);

      if (isCycleEnded || report.isSupplemental) return;

      if (canReviewReports) {
        const wasRead = readSet.has(report.id);
        if (!wasRead) {
          onReportRead(report.id);
        }

        const logged = loggedReportsRef.current;
        const shouldLogView =
          onReportViewed &&
          logged &&
          !logged.has(report.id) &&
          !persistedReadIds.has(report.id);

        if (shouldLogView) {
          logged.add(report.id);

          const rollback = () => {
            logged.delete(report.id);
            if (!wasRead && onReportUnread) {
              onReportUnread(report.id);
            }
          };

          const runView = async (overrideLockId?: string) => {
            const ok = await onReportViewed!(report.id, overrideLockId);
            if (!ok) rollback();
          };

          if (
            !autoLockFiredRef.current &&
            !hasLockedRevision &&
            onAutoLock &&
            mostRecentRevisionId
          ) {
            autoLockFiredRef.current = true;
            onAutoLock(
              mostRecentRevisionId,
              (newRevisionId) => {
                runView(newRevisionId);
              },
              () => {
                rollback();
                autoLockFiredRef.current = false;
              }
            );
          } else {
            runView();
          }
        }
      }
    },
    [
      isCycleEnded,
      canReviewReports,
      hasLockedRevision,
      onAutoLock,
      mostRecentRevisionId,
      readSet,
      persistedReadIds,
      onReportRead,
      onReportUnread,
      onReportViewed,
    ]
  );

  const withStatus = (reports: Report[]): ReportWithStatus[] =>
    reports.map((r) => ({ ...r, isRead: readSet.has(r.id) }));

  const allReports = [...aggregateReports, ...supplementalReports];
  const aggregateReportsWithStatus = withStatus(aggregateReports);
  const supplementalReportsWithStatus = withStatus(supplementalReports);
  const selectedReport = allReports.find((r) => r.id === selectedReportId);
  const totalRead = aggregateReports.filter((r) => readSet.has(r.id)).length;
  const totalReports = aggregateReports.length;

  const reportUrl = selectedReport
    ? buildReportUrl(
        selectedReport.serverURL,
        reportingHistoryId,
        reportMonthYear,
        selectedReport.usesDynamicParameters
      )
    : "";

  return (
    <div>
      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          fontSize: "1.05rem",
          color: "#334155",
          paddingTop: "1.5rem",
          marginBottom: "1.75rem",
        }}
      >
        <span className="fw-medium">
          Click on a report to view it.
          {!isCycleEnded && " Reports must be reviewed before submission."}
        </span>

        {canCertify && !isCycleEnded && (
          <span className="fw-semibold" style={{ color: "#334155" }}>
            {totalRead} / {totalReports} reviewed
          </span>
        )}
      </div>

      <div className="row g-4 mb-4">
        <div className={supplementalReports.length > 0 ? "col-md-6" : "col-12"}>
          <div className="reports-tab__panel">
            <SectionHeader
              icon={<FiBarChart2 size={20} style={{ color: "#ffffff" }} />}
              title="Aggregate Reports"
              count={aggregateReports.length}
            />
            <div>
              {aggregateReportsWithStatus.map((report) => (
                <ReportRow
                  key={report.id}
                  report={report}
                  isSelected={selectedReportId === report.id}
                  canReviewReports={canReviewReports}
                  onClick={() => handleSelectReport(report)}
                />
              ))}
            </div>
          </div>
        </div>

        {supplementalReports.length > 0 && (
          <div className="col-md-6">
            <div className="reports-tab__panel">
              <SectionHeader
                icon={<FiClipboard size={20} style={{ color: "#ffffff" }} />}
                title="Supplemental Reports"
                count={supplementalReports.length}
              />
              <div>
                {supplementalReportsWithStatus.map((report) => (
                  <ReportRow
                    key={report.id}
                    report={report}
                    isSelected={selectedReportId === report.id}
                    canReviewReports={canReviewReports}
                    onClick={() => handleSelectReport(report)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedReport ? (
        <div className="reports-tab__panel">
          <div className="reports-tab__viewer-header">
            <FiBarChart2 size={16} />
            {selectedReport.label}
          </div>

          {cookieError ? (
            <div className="reports-tab__placeholder reports-tab__placeholder--error">
              Failed to load report. Please refresh and try again.
            </div>
          ) : !cookieReady ? (
            <div className="reports-tab__placeholder">Loading report...</div>
          ) : reportUrl ? (
            <ReportFrame
              src={reportUrl}
              title={selectedReport.label}
              height="600px"
            />
          ) : (
            <div className="reports-tab__placeholder reports-tab__placeholder--dashed">
              This report is currently unavailable.
            </div>
          )}
        </div>
      ) : (
        <div className="reports-tab__placeholder reports-tab__placeholder--dashed">
          Select a report above to preview it here
        </div>
      )}
    </div>
  );
}
